import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { InputField } from '../InputField';
import { GoogleLogin } from '@react-oauth/google';
import { ParentData, RegistrationFormData, SchoolModel, Subject } from '../../model/registration/SchoolSelection';
import { SignUpRequest } from '../../model/registration/SignUpRequest';
import { getReferralToken, clearReferralToken } from '../../utils/referral';
import useAuth from '../../hooks/useAuth';
import { useLoading } from '../../context/LoadingContext';
import { useError } from '../../context/ErrorContext';
import api from '../../utils/api';
import ReactMarkdown from 'react-markdown';
import { decode } from 'html-entities';
import CloseButton from '../modal/CloseButton';

interface ParentRegistrationFormProps {
  onSubmit: () => void;
  registrationData: RegistrationFormData;
  onboardingData: SchoolModel;
  updateParentData: <T extends keyof ParentData>(field: T, value: ParentData[T]) => void;
  isGoogleFlow?: boolean;
}

const ParentRegistrationForm: React.FC<ParentRegistrationFormProps> = ({
  updateParentData,
  onSubmit,
  registrationData,
  onboardingData,
  isGoogleFlow = false,
}) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isDisabled, setIsDisabled] = useState(true);
  const [lang, setLang] = useState<string>('ita');
  const [contractData, setContractData] = useState<any>(null);
  const [showContractModal, setShowContractModal] = useState<boolean>(false);
  const [termsAndConditions, setTermsAndConditions] = useState<number>(0);

  const openContractModal = () => setShowContractModal(true);
  const closeContractModal = () => setShowContractModal(false);

  const handleAcceptTerms = () => {
    setTermsAndConditions(1);
    setShowContractModal(false);
  };

  const {
    parentData,
    selectedSchoolClass,
    selectedSchoolSubjects,
    selectedSchoolLevel,
    selectedSchoolType,
    selectedSchoolAddress,
  } = registrationData;

  const { email } = parentData;

  const childSchoolId = onboardingData[selectedSchoolLevel][selectedSchoolType].find(
    (school) => school.name === selectedSchoolAddress,
  )?.id;

  if (!childSchoolId) throw new Error('School not found');

  const navigate = useNavigate();
  const { register, loginWithGoogle } = useAuth();
  const { setLoading } = useLoading();
  const { setError } = useError();

  const school = onboardingData[selectedSchoolLevel]?.[selectedSchoolType]?.find(
    (s) => s.name === selectedSchoolAddress,
  );

  const handleGoogleSuccess = async (credentialResponse: any) => {
    setErrors({});
    setLoading(true);
    let termsAccepted = false;
    try {
      const token = credentialResponse.credential;
      await loginWithGoogle(token, 'family');

      await api.post('/auth/accept-terms', { terms_and_conditions: 1 });
      termsAccepted = true;

      const subjectIds = (school?.subjects || [])
        .filter((s: Subject) => selectedSchoolSubjects.includes(s.name))
        .map((s: Subject) => s.id);

      await api.post('/family/student', {
        school_id: school?.id,
        school_class: selectedSchoolClass,
        subject_ids: subjectIds,
      });

      localStorage.removeItem('registrationForm');
      navigate('/family/home');
    } catch (err: any) {
      if (termsAccepted) {
        api.post('/auth/accept-terms', { terms_and_conditions: 0 }).catch(() => {});
      }
      setError(true, err.response?.data?.message || err.message, err.response?.status || 500);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError(true, 'Errore con Google Sign-In. Riprova o usa email e password.', 500);
  };

  const validateEmail = (email: string) => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return 'Email non valida.';
    }
    return '';
  };

  const validatePassword = (password: string) => {
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{8,}$/.test(password)) {
      return 'La password deve contenere almeno 8 caratteri, con almeno una lettera maiuscola, una minuscola, un numero e un carattere speciale.';
    }
    return '';
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    let error = '';
    switch (name) {
      case 'email':
        updateParentData('email', value);
        error = validateEmail(value);
        break;
      case 'password':
        setPassword(value);
        error = validatePassword(value);
        break;
      default:
        break;
    }
    setErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
  };

  useEffect(() => {
    const isFormComplete = email && password && termsAndConditions === 1;
    setIsDisabled(!isFormComplete || Object.values(errors).some((error) => error));
  }, [email, password, errors, termsAndConditions]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (termsAndConditions !== 1) {
      return;
    }
    try {
      let request: SignUpRequest = {
        parent_email: email,
        parent_password: password,
        child_school_id: childSchoolId,
        child_school_class: selectedSchoolClass,
        child_subjects: selectedSchoolSubjects,
        terms_and_conditions: termsAndConditions,
      };
      const referralToken = getReferralToken();
      if (referralToken) {
        request.referral_token = referralToken;
      }
      setLoading(true);
      const response = await register(request, 'family');
      console.log(response);
      if (response.status === 200) {
        clearReferralToken();
        onSubmit();
      }
    } catch (error) {
      console.error(error);
      setError(true, error.response.data.message, error.response.status);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api
      .get(`/contract/cgc/family/${lang}`, { skipAuth: true })
      .then((response) => {
        console.log('Contratti:', response.data);
        setContractData(response.data);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [lang, setError]);

  useEffect(() => {
    if (showContractModal) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [showContractModal]);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeContractModal();
      }
    };

    if (showContractModal) {
      window.addEventListener('keydown', handleEsc);
    } else {
      window.removeEventListener('keydown', handleEsc);
    }

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [showContractModal]);

  // Auto-open modal when contract data is loaded
  useEffect(() => {
    if (contractData && termsAndConditions === 0) {
      setShowContractModal(true);
    }
  }, [contractData]);

  const fixMalformedEntities = (str: string): string => {
    return str
      .replace(/&ldquo/g, '&ldquo;')
      .replace(/&rdquo/g, '&rdquo;')
      .replace(/&ldquopiattaforma/g, '&ldquo;paiatappaforma&rdquo;');
  };

  return (
    <div className="w-full bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="bg-gradient-to-r from-fpcred to-buttonhover p-4 sm:p-6 lg:p-8">
        <div className="text-center text-white">
          <h1 className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold mb-1 sm:mb-2">
            Stai per prenotare la tua prima lezione!
          </h1>
          {/* <p className="text-xs sm:text-sm lg:text-base opacity-90">
            Completa la registrazione per programmare la lezione
          </p> */}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-4 sm:p-6 lg:p-8">
        <div className="max-w-md mx-auto space-y-4 sm:space-y-6">
          <InputField
            id="email"
            name="email"
            type="email"
            placeholder="Email"
            value={email}
            className={`w-full ${errors.email ? 'border-red-500' : ''}`}
            onChange={handleChange}
            error={errors.email}
            autocomplete="email"
            required
          />
          <InputField
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Crea password"
            value={password}
            className={`w-full ${errors.password ? 'border-red-500' : ''}`}
            onChange={handleChange}
            error={errors.password}
            autocomplete="new-password"
            required
            showPassword={showPassword}
            toggleShowPassword={() => setShowPassword((prev) => !prev)}
          />
        </div>

        <div className="border-gray-200 mt-6">
          <div className="flex justify-center">
            <button
              type="submit"
              className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-fpcred text-white text-base sm:text-lg font-semibold rounded-xl hover:bg-buttonhover disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 shadow-lg disabled:shadow-none disabled:transform-none"
              disabled={isDisabled}>
              Procedi
            </button>
          </div>

          {!isGoogleFlow && (
            <>
              <div className="flex items-center gap-2 my-4">
                <div className="flex-1 border-t border-gray-300"></div>
                <span className="text-gray-500 text-sm">oppure</span>
                <div className="flex-1 border-t border-gray-300"></div>
              </div>

              <div
                className={`flex justify-center ${termsAndConditions !== 1 ? 'pointer-events-none opacity-50' : ''}`}>
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  text="signup_with"
                  size="large"
                  width="320"
                  use_fedcm_for_prompt={false}
                />
              </div>
              <div className="flex justify-center mt-4">
                {termsAndConditions === 1 ? (
                  <div className="flex items-center gap-2 text-green-600 font-medium text-sm">
                    <span>✓ Condizioni d'uso accettate</span>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="px-4 py-2 bg-fpcpink text-fpcred hover:bg-fpcred hover:text-white font-medium rounded-lg transition-all duration-200 border-2 border-fpcred"
                    onClick={openContractModal}>
                    Leggi le condizioni d'uso
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </form>
      <div className="px-4 sm:px-6 lg:px-8 pb-4 sm:pb-6 lg:pb-8">
        <div className="max-w-md mx-auto sm:-mt-1 rounded-2xl border border-fpcred/25 bg-gradient-to-r from-fpcpink/40 to-white p-4 sm:p-5 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-center sm:text-left">
              <p className="text-sm sm:text-base font-semibold text-fpdarkred">Preferisci parlare con qualcuno?</p>
              <p className="text-xs sm:text-sm text-gray-600">Ti richiamiamo noi!</p>
            </div>
            <button
              type="button"
              className="w-full sm:w-auto px-5 sm:px-6 py-2.5 sm:py-3 bg-fpcred text-white text-sm sm:text-base font-semibold rounded-xl hover:bg-buttonhover transition-all duration-200 transform hover:-translate-y-0.5 shadow-md"
              onClick={() => window.open('https://forms.gle/qzrP5fmLCVkH4pfP6', '_blank', 'noopener,noreferrer')}>
              <span className="mr-2" aria-hidden="true">
                📞
              </span>
              Contattaci
            </button>
          </div>
        </div>
      </div>

      {/* Modal del contratto */}
      {showContractModal && contractData && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-[10000]"
          onClick={closeContractModal}>
          <div
            className="bg-white rounded-md w-11/12 max-w-4xl max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="p-6 pb-0 flex-shrink-0 relative">
              <CloseButton onClick={closeContractModal} className="absolute top-4 right-4" />
              <h1 className="text-2xl font-bold mb-2 pr-14">{contractData.title}</h1>
            </div>

            {/* Scrollable content */}
            <div className="px-6 overflow-y-auto flex-1 min-h-0">
              <div className="mb-4">
                <h3 className="font-semibold">Accettazione</h3>
                {contractData && contractData.acceptance && (
                  <ReactMarkdown>{decode(fixMalformedEntities(contractData.acceptance))}</ReactMarkdown>
                )}
              </div>

              {contractData.sections?.map((section: any, idx: number) => {
                const sectionNumber = idx + 1;
                return (
                  <div key={idx} className="mb-4">
                    <h3 className="font-bold mb-1">
                      {sectionNumber}. {section.title}
                    </h3>
                    {section.content && <ReactMarkdown>{decode(fixMalformedEntities(section.content))}</ReactMarkdown>}

                    {section.subsections?.map((sub: any, sIdx: number) => {
                      const subSectionNumber = `${sectionNumber}.${sIdx + 1}`;
                      return (
                        <div key={sIdx} className="ml-4 mt-2 flex gap-2 border-l pl-4 border-gray-300">
                          {sub.title ? (
                            <h4 className="font-semibold">
                              {subSectionNumber}. {sub.title}
                            </h4>
                          ) : (
                            <p className="font-semibold">{subSectionNumber}.</p>
                          )}
                          {sub.content && <ReactMarkdown>{decode(fixMalformedEntities(sub.content))}</ReactMarkdown>}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            {/* Fixed bottom button */}
            <div className="p-6 pt-4 border-t border-gray-200 flex-shrink-0 flex justify-center">
              {termsAndConditions === 1 ? (
                <button
                  type="button"
                  onClick={closeContractModal}
                  className="bg-fpcred text-white font-bold py-2 px-6 rounded-md hover:bg-fpdarkred">
                  Chiudi
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleAcceptTerms}
                  className="bg-fpcred text-white font-bold py-3 px-8 rounded-md hover:bg-fpdarkred text-lg">
                  Accetto le condizioni d'uso
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentRegistrationForm;
